<template>
    <div
        class="relative grid gap-4 place-items-center min-h-[900px] -mt-12"
        :style="wheelScale < 1 ? { transform: `scale(${wheelScale})`, transformOrigin: 'top center', marginBottom: `${-(900 * (1 - wheelScale))}px` } : {}"
    >
        <audio ref="spinSound" :src="spinSoundSrc" preload="auto" />
        <audio ref="winningSound" :src="winningSoundSrc" preload="auto" />
        <audio ref="bgMusic" :src="bgMusicSrc" preload="auto" loop />
        <WheelSoundToggle :sound-muted="soundMuted" :sound-playing="soundPlaying" :wave-heights="waveHeights" @toggle="toggleSound" />

        <!-- Pointer (oben) -->
        <div class="absolute z-10 top-[101px]">
            <img :src="$asset('/hkcm/images/wheelArrow.svg', 'public')" alt="Zeiger" class="h-[130px] w-auto" />
        </div>

        <!-- Outer gold ring -->
        <div
            class="absolute rounded-full pointer-events-none w-[652px] h-[652px]"
            style="
                background: linear-gradient(170.25deg, #dcc149 27.4%, #fefce3 36.72%, #dcc149 44.46%, #a7722d 62.83%, #fff2b5 72.85%, #c0a83d 82.81%);
                filter: drop-shadow(0px 4px 16.1px rgba(0, 0, 0, 0.57));
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
            "
        />

        <!-- Blue ring -->
        <div
            class="absolute rounded-full pointer-events-none w-[629px] h-[629px]"
            style="background: linear-gradient(252.52deg, #031837 7.19%, #353cee 26.44%, #031837 73.36%, #353cee 90.6%); box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25)"
        />

        <!-- Inner gold ring (border around wheel) -->
        <div
            class="absolute rounded-full pointer-events-none w-[589px] h-[589px]"
            style="
                background: linear-gradient(170.25deg, #dcc149 27.4%, #fefce3 36.72%, #dcc149 44.46%, #a7722d 62.83%, #fff2b5 72.85%, #c0a83d 82.81%);
                filter: drop-shadow(0px 4px 16.1px rgba(0, 0, 0, 0.57));
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
            "
        />

        <!-- Wheel -->
        <div
            class="absolute w-[574px] h-[574px] rounded-full overflow-hidden transition-transform ease-[cubic-bezier(0.17,0.67,0.22,1)]"
            :class="`duration-[5000ms]`"
            :style="{ transform: `rotate(${rotation}deg)` }"
        >
            <div v-for="(s, i) in segments" :key="s.code" class="absolute w-1/2 h-1/2 left-1/2 top-1/2 origin-top-left" :style="segStyle(i)">
                <span
                    class="flex flex-col gap-1 items-center text-center uppercase absolute left-[6px]"
                    :class="i % 2 === 0 ? 'text-[#031837]' : 'text-white'"
                    :style="{
                        top: segmentTopPx + 'px',
                        width: segmentWidthPx + 'px',
                        transform: `skewY(calc(-90deg + ${segAngle}deg)) rotate(155deg) translate(${s.translate})`
                    }"
                >
                    <img :src="s.img" alt="" :style="{ width: segmentImgSizePx + 'px', height: segmentImgSizePx + 'px' }" class="object-contain drop-shadow-lg" />
                    <span :class="s.fontSize" v-html="s.title"></span>
                </span>
                <div class="block h-[300px]"></div>
            </div>
        </div>

        <!-- Center: HKCM logo (outside rotating div to prevent rasterization) -->
        <div class="absolute z-10 cursor-pointer left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[176px] h-[176px]" @click="onSpinClick">
            <img :src="$asset('/hkcm/images/WheelOfFortuneCenter.png', 'public')" alt="HKCM" class="w-full h-full" />
        </div>

        <!-- Outer bulbs -->
        <div class="absolute w-[652px] h-[652px] rotate-[157deg]">
            <div v-for="b in bulbs" :key="b.key" class="absolute rounded-full" :style="b.style" aria-hidden="true" />
        </div>

        <!-- Spin Button -->
        <div class="absolute bottom-12">
            <button
                class="relative w-[340px] h-[60px] rounded-full cursor-pointer select-none overflow-hidden disabled:cursor-not-allowed disabled:opacity-60 group"
                style="
                    background: linear-gradient(135deg, #2a85ff 0%, #1a5fd4 60%, #0d3fa0 100%);
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    box-shadow:
                        0 0 32px rgba(29, 120, 255, 0.45),
                        0 8px 28px rgba(0, 0, 0, 0.45),
                        inset 0 1px 0 rgba(255, 255, 255, 0.18);
                    transition:
                        box-shadow 0.2s ease,
                        transform 0.15s ease;
                "
                :disabled="spinning || done"
                @click="onSpinClick"
            >
                <!-- Shimmer overlay -->
                <span
                    class="pointer-events-none absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[linear-gradient(105deg,transparent_40%,rgba(255,255,255,0.12)_50%,transparent_60%)]"
                ></span>
                <span class="relative flex items-center justify-center gap-2 uppercase text-white font-bold text-lg tracking-widest">
                    {{ done ? 'Fertig' : 'Jetzt drehen' }}
                </span>
            </button>
        </div>

        <WheelWinModal :open="priceModal" :current-prize="currentPrize" :current-prize-img="currentPrizeImg" @close="priceModal = false" />
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { postJSON } from '../../http.js';
import { asset } from '../../helpers.js';
import { WAVE_HEIGHTS, createWheelAudioController } from './wheelAudio.js';
import { createBulbs, getBulbStyle } from './wheelBulbs.js';
import WheelSoundToggle from './WheelSoundToggle.vue';
import WheelWinModal from './WheelWinModal.vue';

const props = defineProps({
    token: { type: String, required: true },
    endpoint: { type: String, default: '/wheel/spin' }
});

const bgMusicSrc = asset('/hkcm/music/WheelOfFortune_backgoundMusic.mp3', 'public');
const spinSoundSrc = asset('/hkcm/music/WheelOfFortune_sound.mp3', 'public');
const winningSoundSrc = asset('/hkcm/music/WheelOfFortune_winningSound.mp3', 'public');

const windowWidth = ref(window.innerWidth);
// Continuous scale based on desktop wheel size so behavior stays identical below/above old breakpoint.
const wheelScale = computed(() => {
    // desktop ring = 652px — scale so ring + 16px padding fits viewport
    return Math.min(1, windowWidth.value / 668);
});

// Keep content metrics in desktop proportions and let the container scale continuously.
const segmentTopPx = computed(() => 158);
const segmentWidthPx = computed(() => 144);
const segmentImgSizePx = computed(() => 100);
const spinSound = ref(null);
const winningSound = ref(null);
const bgMusic = ref(null);
const soundMuted = ref(false);
const soundPlaying = ref(false);
const waveHeights = WAVE_HEIGHTS;

onMounted(() => {
    setVolumes();
    startBgMusic();
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    const onFirstInteraction = () => {
        startBgMusic();
        events.forEach((e) => document.removeEventListener(e, onFirstInteraction));
    };
    events.forEach((e) => document.addEventListener(e, onFirstInteraction, { once: true }));
});

onBeforeUnmount(() => {
    stopAllSounds();
});

window.addEventListener('resize', () => {
    windowWidth.value = window.innerWidth;
});

const currentToken = ref(props.token);

const segments = [
    { code: 'DACIA', title: 'Hyundai</br><span style="text-transform:none">i10</span>', img: asset('/hkcm/images/Preis_1_Hyundaii10.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'LIFETIME', title: 'Lifetime</br>HKCM-Abo', img: asset('/hkcm/images/Preis_2_Lifetimeabo.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'CASH_3000', title: '3.000 €</br>Cash', img: asset('/hkcm/images/Preis_3_3000Cash.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'GOLD_COIN', title: 'Gold-</br>münze', img: asset('/hkcm/images/Preis_4_Goldmuenze.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'CASH_1000', title: '1.000 €</br>Cash', img: asset('/hkcm/images/Preis_5_1000Cash.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'SILVER_4OZ', title: 'Silbermünze</br>4 oz', img: asset('/hkcm/images/Preis_6_Silbermuenze4oz.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'ABO_3M', title: '3-Monats-</br>abo', img: asset('/hkcm/images/Preis_8_3Monatsabo.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' },
    { code: 'SILVER_1OZ', title: 'Silbermünze</br>1 oz', img: asset('/hkcm/images/Preis_7_Silbermuenze1oz.png', 'public'), fontSize: 'text-xxs xs:text-xs font-bold', translate: '-48px, 109px' }
];

const rotation = ref(0);
const spinning = ref(false);
const done = ref(false);
const msg = ref('');
const priceModal = ref(false);
const currentPrize = ref(null);
const currentPrizeImg = computed(() => segments.find((s) => s.code === currentPrize.value?.prize_code)?.img ?? null);

const segAngle = 360 / segments.length;
const segStyle = (i) => {
    const isGold = i % 2 === 0;

    return {
        transform: `rotate(${i * segAngle}deg) skewY(${90 - segAngle}deg)`,
        background: isGold
            ? 'radial-gradient(circle at 0% 0%, #EDD875 0%, #DCC149 25%, #A7722D 55%, #2E1A04 100%)'
            : 'radial-gradient(circle at 0% 0%, #5056CC 0%, #353CEE 25%, #1D2A97 60%, #000408 100%)',
        border: '1px solid #FFFFFF',
        boxShadow: isGold ? 'inset -8px -16px 21px rgba(0, 0, 0, 0.41)' : 'none'
    };
};

const POINTER_ANGLE = 90;
const FULL_SPINS = 6;
const SAFETY = 9;
const spinDuration = 5000;
const WHEEL_OFFSET = 130;
const bulbDefs = createBulbs();
const bulbs = computed(() => bulbDefs.map((b) => ({ ...b, style: getBulbStyle(b, windowWidth.value) })));

const { setVolumes, startBgMusic, toggleSound, playSpinSound, playWinningSound, stopAllSounds } = createWheelAudioController({
    spinSound,
    winningSound,
    bgMusic,
    soundMuted,
    soundPlaying,
    spinDuration
});

const indexByCode = (code) =>
    Math.max(
        0,
        segments.findIndex((s) => s.code === code)
    );

const onSpinClick = async () => {
    if (spinning.value || done.value) return;

    rotation.value = 0;

    spinning.value = true;
    msg.value = '';

    try {
        const data = await postJSON(props.endpoint, { token: currentToken.value });

        currentPrize.value = data.result;
        playSpinSound();

        const idx = indexByCode(data.result.prize_code);

        const centerAngle = idx * segAngle + segAngle / 2;
        const baseToPointer = POINTER_ANGLE + WHEEL_OFFSET - centerAngle;
        const jitter = Math.random() * (segAngle - 2 * SAFETY) - (segAngle / 2 - SAFETY);
        const delta = FULL_SPINS * 360 + baseToPointer + jitter;
        rotation.value += delta;

        setTimeout(() => {
            done.value = true;
        }, spinDuration);
        setTimeout(() => {
            msg.value = `Gewonnen: ${data.result.prize_title}`;
            playWinningSound();
            priceModal.value = true;
            spinning.value = false;
        }, 9000);
    } catch (e) {
        msg.value = e.message || 'Spin failed';
    }
};
</script>
<style scoped>
@keyframes bulbGlow {
    0% {
        filter: brightness(0.45);
    }
    50% {
        filter: brightness(1.65);
    }
    100% {
        filter: brightness(0.45);
    }
}
</style>
